/**
 * Calendar heatmap for attendance data.
 * Expects daily array: [{ date: 'YYYY-MM-DD', status: 'on_time'|'late'|'very_late'|'absent' }]
 */
import { format, parseISO, eachDayOfInterval, startOfMonth, endOfMonth, getDay } from 'date-fns'
import { ru } from 'date-fns/locale'

const STATUS_COLOR = {
  on_time: 'bg-green-500',
  late: 'bg-yellow-400',
  very_late: 'bg-red-400',
  absent: 'bg-red-600',
  no_schedule: 'bg-gray-200',
}
const STATUS_LABEL = {
  on_time: 'Вовремя',
  late: 'Опоздал',
  very_late: 'Сильн. опозд.',
  absent: 'Не явился',
  no_schedule: 'Нет расписания',
}
const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

export default function AttendanceHeatmap({ daily = [], dateFrom, dateTo }) {
  if (!dateFrom || !dateTo) return null

  const start = parseISO(dateFrom)
  const end = parseISO(dateTo)

  // Group by month
  const byDate = {}
  daily.forEach((d) => {
    byDate[d.date] = d.status
  })

  // Split into months
  const months = []
  let cur = startOfMonth(start)
  while (cur <= end) {
    const mEnd = endOfMonth(cur)
    const days = eachDayOfInterval({
      start: cur < start ? start : cur,
      end: mEnd > end ? end : mEnd,
    })
    months.push({ month: cur, days })
    const next = new Date(cur)
    next.setMonth(next.getMonth() + 1)
    cur = startOfMonth(next)
  }

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm ${STATUS_COLOR[k] || 'bg-gray-200'}`} />
            {v}
          </div>
        ))}
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200" />
          Выходной
        </div>
      </div>

      {months.map(({ month, days }) => (
        <div key={month.toISOString()}>
          <p className="text-sm font-medium text-gray-600 mb-2 capitalize">
            {format(month, 'LLLL yyyy', { locale: ru })}
          </p>
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-xs text-gray-400 py-0.5">{d}</div>
            ))}
            {/* Padding for first week */}
            {Array.from({ length: (getDay(days[0]) + 6) % 7 }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd')
              const status = byDate[key]
              const isWeekend = getDay(day) === 0 || getDay(day) === 6
              return (
                <div
                  key={key}
                  title={`${format(day, 'dd.MM.yyyy')}${status ? ': ' + (STATUS_LABEL[status] || status) : ''}`}
                  className={`w-full aspect-square rounded-sm text-center flex items-center justify-center text-xs font-medium
                    ${status ? STATUS_COLOR[status] + ' text-white' : isWeekend ? 'bg-gray-50 text-gray-300' : 'bg-gray-100 text-gray-400'}
                  `}
                >
                  {format(day, 'd')}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
